<template>
  <div class="email-verification-page">
    <div class="email-verification-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Verifying your email address...</p>
      </div>

      <div v-else-if="success" class="success-message">
        <div class="success-icon">✓</div>
        <h1>Welcome to Dietedics!</h1>
        <p>Thank you for verifying your email address.</p>
        <p class="redirect-message">Redirecting you to the search page in {{ countdown }} seconds...</p>
      </div>

      <div v-else-if="error" class="error-message">
        <div class="error-icon">✗</div>
        <h2>Verification Failed</h2>
        <p>{{ error }}</p>
        <div class="error-actions">
          <router-link to="/" class="back-link">
            Go to Home
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiRequest } from '../../config/api.js'
import { handleApiError } from '../../services/errorHandler.js'

export default {
  name: 'EmailVerification',
  data() {
    return {
      loading: true,
      success: false,
      error: null,
      countdown: 5
    }
  },
  mounted() {
    // Check if backend already verified (redirected with verified=true)
    const verified = this.$route.query.verified === 'true'
    const error = this.$route.query.error

    if (verified) {
      // Backend already verified, just show success
      this.success = true
      this.loading = false
      this.startCountdown()
    } else if (error) {
      // Backend returned an error
      this.error = decodeURIComponent(error)
      this.loading = false
    } else {
      // Need to verify via API
      this.verifyEmail()
    }
  },
  methods: {
    async verifyEmail() {
      // Get confirmation token from URL query parameter
      const confirmationToken = this.$route.query.confirmation_token

      if (!confirmationToken) {
        this.error = 'Invalid or missing confirmation token. Please check your email and try again.'
        this.loading = false
        return
      }

      try {
        // Call the API to verify the email
        const response = await apiRequest(`/users/confirmation?confirmation_token=${encodeURIComponent(confirmationToken)}`, {
          method: 'GET'
        })

        if (response.status && response.status.code === 200) {
          this.success = true
          this.startCountdown()
        } else {
          this.error = response.status?.message || 'Email verification failed. Please try again.'
        }
      } catch (error) {
        const errorInfo = handleApiError(error, { silent: true })
        this.error = errorInfo.messages[0] || 'Email verification failed. Please try again or request a new confirmation email.'
      } finally {
        this.loading = false
      }
    },
    startCountdown() {
      const interval = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(interval)
          this.$router.push('/search')
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
.email-verification-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: var(--color-background-main);
}

.email-verification-container {
  width: 100%;
  max-width: 600px;
  text-align: center;
}

.loading-state {
  padding: 40px 20px;
  color: var(--color-text-secondary);
}

.spinner {
  border: 3px solid var(--color-border-light);
  border-top: 3px solid var(--color-primary-main);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  background: var(--color-surface-light);
  border-radius: 12px;
  padding: 50px 40px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.success-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--color-success-main);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  margin: 0 auto 30px;
  font-weight: bold;
}

.success-message h1 {
  color: var(--color-text-primary);
  margin-bottom: 20px;
  font-size: 2rem;
  font-weight: 600;
}

.success-message p {
  color: var(--color-text-secondary);
  margin-bottom: 15px;
  font-size: 1.1rem;
  line-height: 1.6;
}

.redirect-message {
  color: var(--color-text-light);
  font-size: 0.95rem;
  margin-top: 20px;
}

.error-message {
  background: var(--color-surface-light);
  border-radius: 12px;
  padding: 50px 40px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.error-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--color-error-main);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  margin: 0 auto 30px;
  font-weight: bold;
}

.error-message h2 {
  color: var(--color-text-primary);
  margin-bottom: 20px;
  font-size: 1.8rem;
  font-weight: 600;
}

.error-message p {
  color: var(--color-text-secondary);
  margin-bottom: 30px;
  font-size: 1rem;
  line-height: 1.6;
}

.error-actions {
  margin-top: 30px;
}

.back-link {
  display: inline-block;
  padding: 12px 24px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all var(--transition-normal);
}

.back-link:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 165, 169, 0.3);
}
</style>
